import { useCallback, useState } from 'react';
import { useApiServices } from '@shared/lib/use-api-services';
import { isApiError } from '@shared/api/errors/errors';
import type {
  AddressControlMode,
  WalletMonitoringAddress,
} from '@shared/api/contracts';

export const useAddressControlMode = () => {
  const services = useApiServices();
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const changeControlMode = async (
    addressId: number,
    mode: AddressControlMode,
    version: number,
    reason?: string,
  ) => {
    setPendingIds((prev) => new Set(prev).add(addressId));
    setError(null);

    try {
      const updated = await services.wallets.changeWalletAddressControlMode(addressId, {
        mode,
        version,
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      });
      return { success: true as const, data: updated };
    } catch (err: unknown) {
      if (isApiError(err)) {
        if (err.status === 409) {
          return { success: false as const, conflict: true as const };
        }
        if (err.status === 404) {
          return { success: false as const, notFound: true as const };
        }
        if (err.status === 422) {
          return {
            success: false as const,
            validation: err.message || 'Validation error',
          };
        }
      }

      const message = (err as { message?: string })?.message || 'Failed to change control mode';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(addressId);
        return next;
      });
    }
  };

  const isPending = (addressId: number) => pendingIds.has(addressId);

  return {
    changeControlMode,
    isPending,
    error,
    clearError,
  };
};

export type ChangeControlModeResult =
  | { success: true; data: WalletMonitoringAddress }
  | { success: false; conflict: true }
  | { success: false; notFound: true }
  | { success: false; validation: string }
  | { success: false; error: string };
